import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useEnv } from '../../context/EnvContext';
import { useGlobalsEditable } from '../../context/useGlobalsEditable';
import { useAppEnv } from '../../api/useAppEnv';
import { READONLY_INTERACTIVE_ATTR } from '../../context/ReadOnlyGuard';
import { colors } from '../../theme/tokens';

/**
 * WAVE H — read-only banner for the GLOBAL / admin-catalog surface (/admin/*).
 *
 * Unlike the tenant ReadOnlyBanner (which reflects per-tenant authoring homes),
 * this banner reflects GLOBALS editability:
 *   - Flag ON  → globals editable on every NON-PROD env. The banner shows only
 *     when globals are NOT editable here, i.e. on prod, or while browsing a
 *     remote env via the dropdown.
 *   - Flag OFF → globals editable only on the env-level editable env (dev01);
 *     the banner shows on every other env — byte-identical to the legacy
 *     env-level read-only experience for these pages.
 *
 * When the read-only is purely because the dropdown points at a remote env that
 * IS itself a globals-editable deployment, we offer a one-click Switch back to
 * this pod's env (where globals ARE editable) — the same escape-hatch UX as the
 * tenant banner.
 *
 * Accessibility: role="status" + aria-live="polite"; not dismissible (the state
 * is environmental).
 */
const GlobalsReadOnlyBanner: React.FC = () => {
  const { currentEnv, setCurrentEnv, availableEnvs, editableEnv } = useEnv();
  const appEnv = useAppEnv();
  const editable = useGlobalsEditable();

  if (editable) return null;
  // Avoid flashing the banner before we know the env (fail-closed in the guard,
  // but the banner shouldn't render a misleading message during load).
  if (!appEnv) return null;

  const flagOn = appEnv.perTenantEditingEnvEnabled === true;
  const podEnv = appEnv.ladderEnv; // the env this deployment actually runs in
  const isProdHere = podEnv === 'prod';

  // Are we read-only only because the dropdown points at a remote env whose pod
  // would otherwise allow globals edits? Offer a Switch back to this pod's env.
  // (Flag-on: any non-prod pod is globals-editable. Flag-off: only the editable
  // env. The home target is podEnv when flag-on/non-prod, else editableEnv.)
  const switchTarget = flagOn ? (isProdHere ? null : podEnv) : editableEnv;
  const isRemoteBrowse = !!switchTarget && currentEnv !== switchTarget;
  const canSwitch =
    isRemoteBrowse && availableEnvs.some((e) => e.name === switchTarget);

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="globals-read-only-banner"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 4,
        py: 1.5,
        backgroundColor: colors.warningBgStrong,
        borderBottom: `1px solid ${colors.warningBorder}`,
      }}
    >
      <LockOutlinedIcon sx={{ color: colors.warningIcon, width: 20, height: 20 }} />
      <Typography
        sx={{
          fontFamily: 'Manrope, sans-serif',
          fontSize: 13,
          fontWeight: 600,
          color: colors.warningText,
          flexGrow: 1,
        }}
      >
        {flagOn && currentEnv === 'prod' ? (
          <>
            Global catalogs are <strong>read-only on prod</strong>. Edit globals
            on any non-prod environment.
          </>
        ) : isRemoteBrowse ? (
          <>
            Read-only view — you are browsing <strong>{currentEnv}</strong>.
            Switch to <strong>{switchTarget}</strong> to edit global catalogs.
          </>
        ) : (
          <>
            Read-only view — global catalogs are not editable on{' '}
            <strong>{currentEnv}</strong>.
          </>
        )}
      </Typography>

      {canSwitch && (
        // Marked interactive so ReadOnlyGuard never freezes this escape hatch.
        <span {...{ [READONLY_INTERACTIVE_ATTR]: 'true' }}>
          <Button
            size="small"
            variant="outlined"
            data-testid="globals-read-only-banner-switch"
            onClick={() => setCurrentEnv(switchTarget as string)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              borderColor: colors.warningIcon,
              color: colors.warningText,
              '&:hover': { borderColor: colors.warningText, backgroundColor: 'transparent' },
            }}
          >
            Switch to {switchTarget}
          </Button>
        </span>
      )}
    </Box>
  );
};

export default GlobalsReadOnlyBanner;
